import { utils, write } from "xlsx";

const MAX_LINHAS_FUNCIONARIOS = 500;

export const COLUNAS_FUNCIONARIOS = [
  "Nome*",
  "CPF",
  "Data de Nascimento",
  "Data de Contratacao",
  "Data de Demissao",
  "Empresa*",
  "Situacao",
  "Observacoes",
];

const COLUNAS_EMPRESAS = [
  "Empresa para Selecao",
  "Nome",
  "Documento",
  "Tipo",
  "Situacao",
];

function normalizarDocumento(empresa) {
  return empresa?.tipo_pessoa === "PF"
    ? empresa?.cpf || ""
    : empresa?.cnpj || empresa?.cpf || "";
}

function normalizarTipoEmpresa(classificacao) {
  if (classificacao === "F") return "Fornecedor";
  if (classificacao === "V") return "Filial";
  if (classificacao === "E") return "Entidade";
  return "Empresa";
}

function normalizarSituacao(situacao) {
  return situacao === "I" ? "Inativo" : "Ativo";
}

function nomeEmpresa(empresa) {
  return empresa?.fantasia || empresa?.razao_social || empresa?.nome || "Empresa sem nome";
}

function empresaParaSelecao(empresa, indice) {
  const nome = nomeEmpresa(empresa);
  const documento = normalizarDocumento(empresa);
  const sufixoDocumento = documento ? ` | ${documento}` : "";
  const sufixoTipo = ` | ${normalizarTipoEmpresa(empresa?.classificacao)}`;
  return `${nome}${sufixoDocumento}${sufixoTipo}${indice ? ` #${indice + 1}` : ""}`;
}

export function montarEmpresasParaModelo(empresas) {
  const contagem = new Map();

  return (Array.isArray(empresas) ? empresas : []).map((empresa) => {
    const base = empresaParaSelecao(empresa, 0);
    const indice = contagem.get(base) || 0;
    contagem.set(base, indice + 1);
    const selecao = indice > 0 ? empresaParaSelecao(empresa, indice) : base;

    return {
      "Empresa para Selecao": selecao,
      Nome: nomeEmpresa(empresa),
      Documento: normalizarDocumento(empresa),
      Tipo: normalizarTipoEmpresa(empresa?.classificacao),
      Situacao: normalizarSituacao(empresa?.situacao),
    };
  });
}

function criarAbaInstrucoes() {
  return utils.aoa_to_sheet([
    ["Modelo de Importacao de Funcionarios"],
    [""],
    ["Como preencher"],
    ["1. Preencha somente a aba Funcionarios."],
    ["2. Nao informe ID. O codigo do funcionario sera gerado automaticamente no cadastro."],
    ["3. Use a lista suspensa da coluna Empresa para escolher uma empresa da aba Empresas Vinculadas."],
    ["4. Datas devem usar o formato AAAA-MM-DD. Exemplo: 2026-07-08."],
    ["5. Situacao aceita Ativo ou Inativo. Se ficar vazia, o sistema podera assumir Ativo na importacao."],
    ["6. A aba Empresas Vinculadas e apenas referencia para evitar digitacao incorreta."],
    [""],
    ["Campos obrigatorios"],
    ["Nome*"],
    ["Empresa*"],
  ]);
}

function criarLinhaExemploFuncionario(empresasModelo) {
  const empresaExemplo = empresasModelo?.[0]?.["Empresa para Selecao"] || "";
  return [
    "Maria Silva",
    "123.456.789-00",
    "1990-05-20",
    "2026-07-08",
    "",
    empresaExemplo,
    "Ativo",
    "Linha de exemplo. Substitua pelos dados reais ou apague antes de importar.",
  ];
}

function criarAbaFuncionarios(empresasModelo) {
  const linhas = [COLUNAS_FUNCIONARIOS, criarLinhaExemploFuncionario(empresasModelo)];
  for (let i = 1; i < MAX_LINHAS_FUNCIONARIOS; i += 1) {
    linhas.push(["", "", "", "", "", "", "Ativo", ""]);
  }

  const sheet = utils.aoa_to_sheet(linhas);
  sheet["!cols"] = [
    { wch: 34 },
    { wch: 16 },
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
    { wch: 48 },
    { wch: 12 },
    { wch: 34 },
  ];
  sheet["!autofilter"] = { ref: `A1:H${MAX_LINHAS_FUNCIONARIOS + 1}` };
  sheet["!freeze"] = { xSplit: 0, ySplit: 1 };
  return sheet;
}

function criarAbaEmpresas(empresasModelo) {
  const linhas = [
    COLUNAS_EMPRESAS,
    ...(empresasModelo.length
      ? empresasModelo.map((empresa) => COLUNAS_EMPRESAS.map((coluna) => empresa[coluna] || ""))
      : [["Cadastre uma filial ou fornecedor antes de preencher funcionarios.", "", "", "", ""]]),
  ];

  const sheet = utils.aoa_to_sheet(linhas);
  sheet["!cols"] = [
    { wch: 58 },
    { wch: 34 },
    { wch: 20 },
    { wch: 14 },
    { wch: 12 },
  ];
  sheet["!autofilter"] = { ref: `A1:E${Math.max(linhas.length, 2)}` };
  sheet["!freeze"] = { xSplit: 0, ySplit: 1 };
  return sheet;
}

export function criarWorkbookModeloFuncionarios(empresas) {
  const empresasModelo = montarEmpresasParaModelo(empresas);
  const workbook = utils.book_new();

  utils.book_append_sheet(workbook, criarAbaInstrucoes(), "Instrucoes");
  utils.book_append_sheet(workbook, criarAbaFuncionarios(empresasModelo), "Funcionarios");
  utils.book_append_sheet(workbook, criarAbaEmpresas(empresasModelo), "Empresas Vinculadas");

  workbook.Props = {
    Title: "Modelo de Importacao de Funcionarios",
    Subject: "Funcionarios",
    Author: "GDoc",
  };

  return { workbook, empresasModelo };
}

function criarTabelaCrc32() {
  const tabela = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    tabela[i] = c >>> 0;
  }
  return tabela;
}

const CRC32_TABLE = criarTabelaCrc32();

function criarTextEncoder() {
  if (typeof TextEncoder !== "undefined") return new TextEncoder();
  const { TextEncoder: NodeTextEncoder } = require("util");
  return new NodeTextEncoder();
}

function criarTextDecoder() {
  if (typeof TextDecoder !== "undefined") return new TextDecoder();
  const { TextDecoder: NodeTextDecoder } = require("util");
  return new NodeTextDecoder();
}

function crc32(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC32_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function readU16(bytes, offset) {
  return bytes[offset] | (bytes[offset + 1] << 8);
}

function readU32(bytes, offset) {
  return (
    bytes[offset] |
    (bytes[offset + 1] << 8) |
    (bytes[offset + 2] << 16) |
    (bytes[offset + 3] << 24)
  ) >>> 0;
}

function writeU16(bytes, offset, value) {
  bytes[offset] = value & 0xff;
  bytes[offset + 1] = (value >>> 8) & 0xff;
}

function writeU32(bytes, offset, value) {
  bytes[offset] = value & 0xff;
  bytes[offset + 1] = (value >>> 8) & 0xff;
  bytes[offset + 2] = (value >>> 16) & 0xff;
  bytes[offset + 3] = (value >>> 24) & 0xff;
}

function concatBytes(partes) {
  const tamanho = partes.reduce((acc, parte) => acc + parte.length, 0);
  const saida = new Uint8Array(tamanho);
  let offset = 0;
  partes.forEach((parte) => {
    saida.set(parte, offset);
    offset += parte.length;
  });
  return saida;
}

function localizarEocd(bytes) {
  for (let i = bytes.length - 22; i >= 0; i -= 1) {
    if (readU32(bytes, i) === 0x06054b50) return i;
  }
  throw new Error("Arquivo XLSX invalido: diretorio ZIP nao encontrado.");
}

function lerZipSemCompressao(arrayBuffer) {
  const bytes = new Uint8Array(arrayBuffer);
  const decoder = criarTextDecoder();
  const eocdOffset = localizarEocd(bytes);
  const totalEntradas = readU16(bytes, eocdOffset + 10);
  let centralOffset = readU32(bytes, eocdOffset + 16);
  const entradas = [];

  for (let i = 0; i < totalEntradas; i += 1) {
    if (readU32(bytes, centralOffset) !== 0x02014b50) {
      throw new Error("Arquivo XLSX invalido: entrada central ZIP invalida.");
    }

    const metodo = readU16(bytes, centralOffset + 10);
    if (metodo !== 0) {
      throw new Error("O modelo XLSX precisa ser gerado sem compressao para aplicar validacoes.");
    }

    const compressedSize = readU32(bytes, centralOffset + 20);
    const nomeLen = readU16(bytes, centralOffset + 28);
    const extraLen = readU16(bytes, centralOffset + 30);
    const commentLen = readU16(bytes, centralOffset + 32);
    const localOffset = readU32(bytes, centralOffset + 42);
    const nome = decoder.decode(bytes.slice(centralOffset + 46, centralOffset + 46 + nomeLen));

    const localNomeLen = readU16(bytes, localOffset + 26);
    const localExtraLen = readU16(bytes, localOffset + 28);
    const dataOffset = localOffset + 30 + localNomeLen + localExtraLen;
    const conteudo = bytes.slice(dataOffset, dataOffset + compressedSize);

    entradas.push({ nome, conteudo });
    centralOffset += 46 + nomeLen + extraLen + commentLen;
  }

  return entradas;
}

function escreverZipSemCompressao(entradas) {
  const encoder = criarTextEncoder();
  const locais = [];
  const centrais = [];
  let offset = 0;

  entradas.forEach((entrada) => {
    const nomeBytes = encoder.encode(entrada.nome);
    const conteudo = entrada.conteudo;
    const crc = crc32(conteudo);

    const local = new Uint8Array(30 + nomeBytes.length);
    writeU32(local, 0, 0x04034b50);
    writeU16(local, 4, 20);
    writeU16(local, 6, 0x0800);
    writeU16(local, 8, 0);
    writeU16(local, 10, 0);
    writeU16(local, 12, 0);
    writeU32(local, 14, crc);
    writeU32(local, 18, conteudo.length);
    writeU32(local, 22, conteudo.length);
    writeU16(local, 26, nomeBytes.length);
    writeU16(local, 28, 0);
    local.set(nomeBytes, 30);
    locais.push(local, conteudo);

    const central = new Uint8Array(46 + nomeBytes.length);
    writeU32(central, 0, 0x02014b50);
    writeU16(central, 4, 20);
    writeU16(central, 6, 20);
    writeU16(central, 8, 0x0800);
    writeU16(central, 10, 0);
    writeU16(central, 12, 0);
    writeU16(central, 14, 0);
    writeU32(central, 16, crc);
    writeU32(central, 20, conteudo.length);
    writeU32(central, 24, conteudo.length);
    writeU16(central, 28, nomeBytes.length);
    writeU16(central, 30, 0);
    writeU16(central, 32, 0);
    writeU16(central, 34, 0);
    writeU16(central, 36, 0);
    writeU32(central, 38, 0);
    writeU32(central, 42, offset);
    central.set(nomeBytes, 46);
    centrais.push(central);

    offset += local.length + conteudo.length;
  });

  const centralOffset = offset;
  const centralBytes = concatBytes(centrais);
  const eocd = new Uint8Array(22);
  writeU32(eocd, 0, 0x06054b50);
  writeU16(eocd, 8, entradas.length);
  writeU16(eocd, 10, entradas.length);
  writeU32(eocd, 12, centralBytes.length);
  writeU32(eocd, 16, centralOffset);
  writeU16(eocd, 20, 0);

  return concatBytes([...locais, centralBytes, eocd]).buffer;
}

function xmlEscape(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function inserirDataValidations(sheetXml, totalEmpresas) {
  const fimEmpresas = Math.max(totalEmpresas + 1, 2);
  const formulaEmpresas = `'Empresas Vinculadas'!$A$2:$A$${fimEmpresas}`;
  const xmlValidacoes = [
    '<dataValidations count="2">',
    '<dataValidation type="list" allowBlank="1" showErrorMessage="1" errorTitle="Empresa invalida" error="Escolha uma empresa da lista Empresas Vinculadas." sqref="F2:F501">',
    `<formula1>${xmlEscape(formulaEmpresas)}</formula1>`,
    "</dataValidation>",
    '<dataValidation type="list" allowBlank="1" showErrorMessage="1" errorTitle="Situacao invalida" error="Use Ativo ou Inativo." sqref="G2:G501">',
    '<formula1>"Ativo,Inativo"</formula1>',
    "</dataValidation>",
    "</dataValidations>",
  ].join("");

  if (sheetXml.includes("<dataValidations")) return sheetXml;

  const tagsDepoisDeValidacoes = [
    "<hyperlinks",
    "<printOptions",
    "<pageMargins",
    "<pageSetup",
    "<headerFooter",
    "<rowBreaks",
    "<colBreaks",
    "<customProperties",
    "<cellWatches",
    "<ignoredErrors",
    "<smartTags",
    "<drawing",
    "<legacyDrawing",
    "<legacyDrawingHF",
    "<picture",
    "<oleObjects",
    "<controls",
    "<webPublishItems",
    "<tableParts",
    "<extLst",
    "</worksheet>",
  ];

  const posicaoInsercao = tagsDepoisDeValidacoes
    .map((tag) => sheetXml.indexOf(tag))
    .filter((posicao) => posicao >= 0)
    .sort((a, b) => a - b)[0];

  if (posicaoInsercao >= 0) {
    return `${sheetXml.slice(0, posicaoInsercao)}${xmlValidacoes}${sheetXml.slice(posicaoInsercao)}`;
  }

  return sheetXml;
}

export function aplicarValidacoesModeloFuncionarios(arrayBuffer, totalEmpresas) {
  const decoder = criarTextDecoder();
  const encoder = criarTextEncoder();
  const entradas = lerZipSemCompressao(arrayBuffer).map((entrada) => {
    if (entrada.nome !== "xl/worksheets/sheet2.xml") return entrada;
    const xml = decoder.decode(entrada.conteudo);
    return {
      ...entrada,
      conteudo: encoder.encode(inserirDataValidations(xml, totalEmpresas)),
    };
  });

  return escreverZipSemCompressao(entradas);
}

function salvarArquivo(arrayBuffer, nomeArquivo) {
  const blob = new Blob([arrayBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = nomeArquivo;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function baixarModeloImportacaoFuncionarios(empresas) {
  const { workbook, empresasModelo } = criarWorkbookModeloFuncionarios(empresas);
  const bruto = write(workbook, { bookType: "xlsx", type: "array", compression: false });
  const validado = aplicarValidacoesModeloFuncionarios(bruto, empresasModelo.length);
  salvarArquivo(validado, "modelo_importacao_funcionarios.xlsx");
}
